import { c as _c } from "react/compiler-runtime";
import { type CSSObject } from '@precis-digital/kurama/theme';
import { TableBodyCell as KuramaTableBodyCell } from '@precis-digital/kurama/Table';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import { type ColumnPinningPosition, flexRender } from '@tanstack/react-table';
import type { ReactElement } from 'react';
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults';
import type { ChartDataRow } from '../../../types';
import TableBodyCellContent from './TableBodyCellContent';
import type { ColumnColorScaleState, TableBodyBaseProps } from '../types';
import { getColorScaleStyle, getColumnCellStyle, getColumnDataKey } from '../utils';
import type { ChartCardTableBodyCellContextResolved, ChartCardTableCell } from '../tanstack';
interface TableBodyCellProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> extends Omit<TableBodyBaseProps<DataKey, DataRow>, 'table' | 'columnPinning' | 'rowSelection' | 'colorScaleColumnMap' | 'isFetchLoading'> {
  cell: ChartCardTableCell<DataKey, DataRow>;
  pinned: ColumnPinningPosition;
  rowSelected: boolean;
  colorScale: ColumnColorScaleState | undefined;
  rowHovered: boolean;
}
const TableBodyCell = t0 => {
  const $ = _c(26);
  const {
    cell: t1,
    pinned,
    rowSelected,
    colorScale,
    totalsAndRangeData,
    elementsDescriptionsMap,
    rowHovered,
    allowColumnResizing,
    enableNullDimensionValue,
    t
  } = t0;
  const {
    id: cellId,
    column,
    getContext,
    row
  } = t1;
  const defaults = useChartCardDefaults();
  const theme = useTheme();
  let cellContent;
  let t2;
  if ($[0] !== colorScale || $[1] !== defaults || $[2] !== elementsDescriptionsMap || $[3] !== enableNullDimensionValue || $[4] !== getContext || $[5] !== row.original || $[6] !== rowSelected || $[7] !== t || $[8] !== theme || $[9] !== totalsAndRangeData) {
    const rawContext = getContext();
    const dataKey = getColumnDataKey(rawContext.column);
    const context = {
      ...rawContext,
      meta: {
        chartCard: {
          elementsDescriptionsMap,
          rowSelected,
          colorScale,
          enableNullDimensionValue,
          t
        }
      }
    };
    cellContent = flexRender(TableBodyCellContent, context);
    t2 = getColorScaleStyle(defaults, colorScale, dataKey, row.original, totalsAndRangeData, theme);
    $[0] = colorScale;
    $[1] = defaults;
    $[2] = elementsDescriptionsMap;
    $[3] = enableNullDimensionValue;
    $[4] = getContext;
    $[5] = row.original;
    $[6] = rowSelected;
    $[7] = t;
    $[8] = theme;
    $[9] = totalsAndRangeData;
    $[10] = cellContent;
    $[11] = t2;
  } else {
    cellContent = $[10];
    t2 = $[11];
  }
  const colorScaleStyle = t2;
  let t3;
  if ($[12] !== allowColumnResizing || $[13] !== column || $[14] !== defaults || $[15] !== pinned || $[16] !== rowHovered || $[17] !== theme) {
    t3 = getColumnCellStyle({
      defaults,
      theme,
      column,
      allowResizing: allowColumnResizing,
      pinned,
      type: "body",
      rowHovered
    });
    $[12] = allowColumnResizing;
    $[13] = column;
    $[14] = defaults;
    $[15] = pinned;
    $[16] = rowHovered;
    $[17] = theme;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const cellStyle = t3;
  const t4 = `calc(var(--col-${column.id}-size) * 1px)`;
  let t5;
  if ($[19] !== allowColumnResizing || $[20] !== cellContent || $[21] !== cellId || $[22] !== cellStyle || $[23] !== colorScaleStyle || $[24] !== t4) {
    t5 = <StyledTableBodyCell key={cellId} allowResizing={allowColumnResizing} width={t4} cellStyle={cellStyle} colorScaleStyle={colorScaleStyle}>{cellContent}</StyledTableBodyCell>;
    $[19] = allowColumnResizing;
    $[20] = cellContent;
    $[21] = cellId;
    $[22] = cellStyle;
    $[23] = colorScaleStyle;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
};
const StyledTableBodyCell = styled(KuramaTableBodyCell, {
  shouldForwardProp: prop => prop !== 'width' && prop !== 'cellStyle' && prop !== 'colorScaleStyle' && prop !== 'allowResizing'
})<{
  allowResizing: boolean;
  width: number | string;
  cellStyle: CSSObject;
  colorScaleStyle: CSSObject | undefined;
}>(({
  theme,
  allowResizing,
  width,
  cellStyle,
  colorScaleStyle
}): CSSObject => ({
  display: 'flex',
  width,
  alignItems: 'center',
  padding: allowResizing ? `${theme.spacing(0)} ${theme.spacing(2)} ${theme.spacing(0)} ${theme.spacing(1)}` : `${theme.spacing(0)} ${theme.spacing(1)}`,
  minHeight: theme.spacing(8),
  ...cellStyle,
  ...colorScaleStyle
}));
export default TableBodyCell;